/**
 * Chat store: mirrors the document's chat array into React, and tracks unread
 * count while the chat tab is not the visible one.
 *
 * Attached and detached alongside presence by state/project.ts, because the
 * Y.Doc is rebuilt on every connect (`initEmptyDocForSync`) — a subscription
 * held across that would be observing a dead document.
 */
import { create } from 'zustand';
import type * as Y from 'yjs';
import { observeChat, postMessage, readChat, type ChatMessage } from '../crdt/chat';
import { localIdentity } from './presence';

interface ChatState {
  messages: ChatMessage[];
  /** Messages that arrived while the chat panel was not open. */
  unread: number;
  /** Whether the chat panel is currently the visible rail tab. */
  open: boolean;
  send(body: string): boolean;
  setOpen(open: boolean): void;
}

let doc: Y.Doc | null = null;
let unobserve: (() => void) | null = null;

export const useChat = create<ChatState>((set) => ({
  messages: [],
  unread: 0,
  open: false,

  /** Returns whether the message was enqueued, so the composer can keep the
   *  draft rather than silently discarding it. */
  send(body) {
    if (!doc) return false;
    postMessage(doc, { kind: 'user', author: localIdentity(), body });
    return true;
  },

  setOpen(open) {
    // Opening the panel is what clears the badge.
    set(open ? { open, unread: 0 } : { open });
  },
}));

export function attachChat(nextDoc: Y.Doc): void {
  detachChat();
  doc = nextDoc;
  const initial = readChat(nextDoc);
  useChat.setState({ messages: initial, unread: 0 });

  unobserve = observeChat(nextDoc, (messages) => {
    useChat.setState((s) => {
      const arrived = messages.length - s.messages.length;
      return {
        messages,
        // Only count genuine arrivals, and only while the panel is closed.
        unread: s.open || arrived <= 0 ? s.unread : s.unread + arrived,
      };
    });
  });
}

export function detachChat(): void {
  unobserve?.();
  unobserve = null;
  doc = null;
  useChat.setState({ messages: [], unread: 0 });
}
